import { oc } from "@orpc/contract";
import { z } from "zod";
import { AIWorkflow, ModuleConfig, ProductModule } from "@erp/shared-types";

// Validation schemas
const productModuleSchema = z.enum(ProductModule);

const moduleConfigSchema: z.ZodType<ModuleConfig> = z.object({
  emailWorkflows: z
    .partialRecord(
      z.enum(AIWorkflow),
      z.object({
        registeredEmailDomains: z.array(
          z.object({
            header: z.enum(["to", "cc"]),
            domain: z.string(),
          }),
        ),
      }),
    )
    .optional(),
  ap: z
    .object({
      invoiceLineItemPurchaseMatching: z.array(z.array(z.string())).optional(),
      paymentScheduling: z
        .object({
          mode: z.enum(["immediate", "payRun", "latestDate"]),
          cron: z.string().optional(),
          immediacy: z.enum(["asap", "latest"]).optional(),
          rounding: z.enum(["up", "down", "nearest"]).optional(),
          timezone: z.string().optional(),
          daysBeforeDue: z.number().positive().optional(),
        })
        .optional(),
    })
    .optional(),
});

const organizationModuleConfigSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  module: productModuleSchema,
  enabled: z.boolean(),
  config: moduleConfigSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});

const createOrUpdateOrganizationModuleConfigInputSchema = z.object({
  module: productModuleSchema,
  enabled: z.boolean().optional(),
  config: moduleConfigSchema,
});

const getModuleConfigsContract = oc
  .output(z.array(organizationModuleConfigSchema))
  .route({
    method: "GET",
    summary: "Get all module configurations for the organization",
  });

const getModuleConfigContract = oc
  .input(z.object({ module: productModuleSchema }))
  .output(organizationModuleConfigSchema)
  .route({
    method: "GET",
    summary: "Get a specific module configuration",
  });

const createOrUpdateModuleConfigContract = oc
  .input(createOrUpdateOrganizationModuleConfigInputSchema)
  .output(organizationModuleConfigSchema)
  .route({
    summary: "Update an existing module configuration",
  });

export const orgConfigModulesContract = {
  getModuleConfigs: getModuleConfigsContract,
  getModuleConfig: getModuleConfigContract,
  createOrUpdateModuleConfig: createOrUpdateModuleConfigContract,
};

export type OrgConfigModulesContract = typeof orgConfigModulesContract;
